import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

const AuthContext = createContext(null);

const safelyParseJson = (value) => {
  if (!value) {
    return null;
  }

  try {
    return JSON.parse(value);
  } catch (error) {
    console.error('Unable to parse stored authentication data.', error);
    return null;
  }
};

const getPermissionCode = (permission) => {
  if (!permission) {
    return '';
  }

  if (typeof permission === 'string') {
    return permission.trim().toUpperCase();
  }

  return String(
    permission.code ||
      permission.permission_code ||
      permission.name ||
      '',
  )
    .trim()
    .toUpperCase();
};

const clearStoredAuthentication = () => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('user');
  localStorage.removeItem('activeRole');
  localStorage.removeItem('school_id');
  localStorage.removeItem('school_profile');
  localStorage.removeItem('school_branding');
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [activeRole, setActiveRoleState] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedUser = safelyParseJson(
      localStorage.getItem('user'),
    );

    const storedActiveRole = safelyParseJson(
      localStorage.getItem('activeRole'),
    );

    if (storedUser) {
      setUser(storedUser);
    }

    if (storedActiveRole) {
      setActiveRoleState(storedActiveRole);
    }

    setLoading(false);
  }, []);

  const handleSetActiveRole = useCallback((role) => {
    if (!role) {
      setActiveRoleState(null);
      localStorage.removeItem('activeRole');
      return;
    }

    setActiveRoleState(role);

    localStorage.setItem(
      'activeRole',
      JSON.stringify(role),
    );
  }, []);

  const login = useCallback(
    (userData, accessToken, refreshToken) => {
      localStorage.setItem(
        'access_token',
        accessToken,
      );

      localStorage.setItem(
        'refresh_token',
        refreshToken,
      );

      localStorage.setItem(
        'user',
        JSON.stringify(userData),
      );

      const schoolId =
        userData?.school_id ||
        userData?.schoolId;

      if (schoolId) {
        localStorage.setItem('school_id', schoolId);
      }

      setUser(userData);

      const roles = Array.isArray(userData?.roles)
        ? userData.roles
        : [];

      if (roles.length === 1) {
        handleSetActiveRole(roles[0]);
      } else {
        handleSetActiveRole(null);
      }
    },
    [handleSetActiveRole],
  );

  const updateUser = useCallback(
    (updatedUser) => {
      if (!updatedUser) {
        return;
      }

      setUser(updatedUser);

      localStorage.setItem(
        'user',
        JSON.stringify(updatedUser),
      );

      const schoolId =
        updatedUser?.school_id ||
        updatedUser?.schoolId;

      if (schoolId) {
        localStorage.setItem('school_id', schoolId);
      }

      const updatedRoles = Array.isArray(
        updatedUser?.roles,
      )
        ? updatedUser.roles
        : [];

      if (activeRole?.id) {
        const refreshedActiveRole = updatedRoles.find(
          (role) => role.id === activeRole.id,
        );

        if (refreshedActiveRole) {
          handleSetActiveRole(refreshedActiveRole);
        } else {
          handleSetActiveRole(null);
        }
      }
    },
    [activeRole, handleSetActiveRole],
  );

  const logout = useCallback(async () => {
    const refreshToken = localStorage.getItem(
      'refresh_token',
    );

    if (refreshToken) {
      try {
        const apiClient = (
          await import('../services/apiClient')
        ).default;

        await apiClient.post('/auth/logout/', {
          refresh: refreshToken,
        });
      } catch (error) {
        console.error('Logout API failed.', error);
      }
    }

    clearStoredAuthentication();

    setUser(null);
    setActiveRoleState(null);
  }, []);

  const permissionCodes = useMemo(() => {
    const permissions = Array.isArray(
      activeRole?.permissions,
    )
      ? activeRole.permissions
      : [];

    return new Set(
      permissions
        .map(getPermissionCode)
        .filter(Boolean),
    );
  }, [activeRole]);

  const isSuperuser = Boolean(
    user?.is_superuser || user?.isSuperuser,
  );

  const hasPermission = useCallback(
    (requiredPermission) => {
      if (!requiredPermission) {
        return true;
      }

      if (isSuperuser) {
        return true;
      }

      const permissionCode =
        getPermissionCode(requiredPermission);

      if (!permissionCode) {
        return true;
      }

      return permissionCodes.has(permissionCode);
    },
    [isSuperuser, permissionCodes],
  );

  const hasAnyPermission = useCallback(
    (requiredPermissions = []) => {
      if (isSuperuser) {
        return true;
      }

      if (!Array.isArray(requiredPermissions)) {
        return hasPermission(requiredPermissions);
      }

      if (requiredPermissions.length === 0) {
        return true;
      }

      return requiredPermissions.some((permission) =>
        hasPermission(permission),
      );
    },
    [hasPermission, isSuperuser],
  );

  const hasAllPermissions = useCallback(
    (requiredPermissions = []) => {
      if (isSuperuser) {
        return true;
      }

      if (!Array.isArray(requiredPermissions)) {
        return hasPermission(requiredPermissions);
      }

      if (requiredPermissions.length === 0) {
        return true;
      }

      return requiredPermissions.every((permission) =>
        hasPermission(permission),
      );
    },
    [hasPermission, isSuperuser],
  );

  const value = useMemo(
    () => ({
      user,
      activeRole,
      loading,
      isSuperuser,
      permissionCodes,
      login,
      logout,
      updateUser,
      setActiveRole: handleSetActiveRole,
      hasPermission,
      hasAnyPermission,
      hasAllPermissions,
    }),
    [
      user,
      activeRole,
      loading,
      isSuperuser,
      permissionCodes,
      login,
      logout,
      updateUser,
      handleSetActiveRole,
      hasPermission,
      hasAnyPermission,
      hasAllPermissions,
    ],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error(
      'useAuth must be used inside AuthProvider.',
    );
  }

  return context;
};